// Browser-side timezone helpers for the effective-from picker.
//
// The server is the authoritative converter (worker/src/tz-utils.js
// via catalogue-client.tz.localToUtc). The browser-side helpers exist
// to render a live preview of "Stored as <UTC>" under the picker so
// the user sees what the system will compare against — and to detect
// DST-spring-forward / fall-back conditions early enough that the
// Submit button can disable itself before the server returns 422.

const ABBREVIATIONS: Record<string, Record<number, string>> = {
  'Australia/Sydney': { 600: 'AEST', 660: 'AEDT' },
  'Pacific/Auckland': { 720: 'NZST', 780: 'NZDT' }
};

function partsToMap(parts: Intl.DateTimeFormatPart[]) {
  const map: Record<string, string> = {};
  for (const part of parts) if (part.type !== 'literal') map[part.type] = part.value;
  return map;
}

function formatInTimezone(utcMs: number, timezone: string) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const parts = partsToMap(formatter.formatToParts(new Date(utcMs)));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second)
  };
}

function getOffsetMinutes(utcMs: number, timezone: string): number {
  const inTz = formatInTimezone(utcMs, timezone);
  const tzAsUtcMs = Date.UTC(inTz.year, inTz.month - 1, inTz.day, inTz.hour, inTz.minute, inTz.second);
  return Math.round((tzAsUtcMs - utcMs) / 60000);
}

function parseLocalIso(localIso: string): { y: number; m: number; d: number; h: number; mi: number; s: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localIso);
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]),
    d: Number(match[3]),
    h: Number(match[4]),
    mi: Number(match[5]),
    s: Number(match[6] ?? '0')
  };
}

export interface TimezoneResolution {
  status: 'ok' | 'non_existent' | 'ambiguous';
  utc?: string;
  tz_abbreviation?: string;
  first_utc?: string;
  second_utc?: string;
  first_abbr?: string;
  second_abbr?: string;
}

export function resolveLocalToUtc(localIso: string, timezone: string): TimezoneResolution {
  const parts = parseLocalIso(localIso);
  if (!parts) return { status: 'non_existent' };

  const wallClockUtcMs = Date.UTC(parts.y, parts.m - 1, parts.d, parts.h, parts.mi, parts.s);
  // Try two candidates: pre-DST (offset earlier in the day) and post-DST.
  const candidateBefore = wallClockUtcMs - 13 * 60 * 60 * 1000;
  const candidateAfter = wallClockUtcMs + 13 * 60 * 60 * 1000;

  const offsetBefore = getOffsetMinutes(candidateBefore, timezone);
  const offsetAfter = getOffsetMinutes(candidateAfter, timezone);

  const utcBefore = wallClockUtcMs - offsetBefore * 60000;
  const utcAfter = wallClockUtcMs - offsetAfter * 60000;

  const inTzBefore = formatInTimezone(utcBefore, timezone);
  const inTzAfter = formatInTimezone(utcAfter, timezone);

  const matchBefore = inTzBefore.year === parts.y && inTzBefore.month === parts.m && inTzBefore.day === parts.d &&
    inTzBefore.hour === parts.h && inTzBefore.minute === parts.mi;
  const matchAfter = inTzAfter.year === parts.y && inTzAfter.month === parts.m && inTzAfter.day === parts.d &&
    inTzAfter.hour === parts.h && inTzAfter.minute === parts.mi;

  if (matchBefore && matchAfter && utcBefore !== utcAfter) {
    const abbrFirst = ABBREVIATIONS[timezone]?.[offsetBefore] ?? '';
    const abbrSecond = ABBREVIATIONS[timezone]?.[offsetAfter] ?? '';
    const earlier = utcBefore < utcAfter ? utcBefore : utcAfter;
    const later = utcBefore < utcAfter ? utcAfter : utcBefore;
    const firstAbbr = utcBefore < utcAfter ? abbrFirst : abbrSecond;
    const secondAbbr = utcBefore < utcAfter ? abbrSecond : abbrFirst;
    return {
      status: 'ambiguous',
      first_utc: new Date(earlier).toISOString(),
      second_utc: new Date(later).toISOString(),
      first_abbr: firstAbbr,
      second_abbr: secondAbbr
    };
  }

  if (matchBefore) {
    return {
      status: 'ok',
      utc: new Date(utcBefore).toISOString(),
      tz_abbreviation: ABBREVIATIONS[timezone]?.[offsetBefore] ?? ''
    };
  }
  if (matchAfter) {
    return {
      status: 'ok',
      utc: new Date(utcAfter).toISOString(),
      tz_abbreviation: ABBREVIATIONS[timezone]?.[offsetAfter] ?? ''
    };
  }
  return { status: 'non_existent' };
}
